import type { WidgetEmbedConfig } from '@syncanix/shared';

/**
 * Idempotently inject the Syncanix widget loader `<script>` into the document.
 *
 * Mirrors the canonical one-tag install (`docs/runbooks/widget-embed.md`): a
 * `type="module"` script pointing at the mutable CDN loader, carrying the
 * publishable key (+ an optional API-origin override). The marker attribute
 * `data-syncanix-embed` makes a second call a no-op (React StrictMode double-
 * invokes effects; the guard prevents two loaders → two launchers).
 *
 * Returns the loader `<script>` — the NEWLY injected one, or, when a marker
 * script already exists, that existing element (never `null` for an existing
 * loader). Returning the existing element is what lets the caller attach its
 * `load` listener on the StrictMode second mount: the first mount injected the
 * script but `window.syncanix` is still undefined, so without a handle to the
 * in-flight script the load event — and the token/context wiring it drives —
 * would be lost. (`null` is returned only in the defensive case where the
 * marker selector matched a non-`<script>` element.)
 *
 * (Browser glue — the pure key→config decision lives in `@syncanix/shared`'s
 * `resolveWidgetEmbed`; this thin DOM insertion is intentionally per-app since
 * `@syncanix/shared` stays environment-free.)
 */
export function injectWidgetScript(
  config: WidgetEmbedConfig,
  doc: Document = document,
): HTMLScriptElement | null {
  const existing = doc.querySelector('script[data-syncanix-embed]');
  if (existing !== null) {
    return existing instanceof HTMLScriptElement ? existing : null;
  }

  const script = doc.createElement('script');
  script.type = 'module';
  script.src = config.scriptSrc;
  script.setAttribute('data-key', config.key);
  if (config.origin !== undefined && config.origin !== '') {
    script.setAttribute('data-origin', config.origin);
  }
  // The CUSTOMER API base — where a capability executes — and the declaration
  // that the same origin is first-party. Emitted TOGETHER on purpose: the
  // widget's B18 trust gate refuses a cross-origin base unless that origin is
  // declared as the tenant's own, so a base without the declaration is refused
  // at the gate rather than dialled. (`data-origin` above is a different axis:
  // the SYNCANIX control plane, for config + the turn stream.)
  if (config.apiBaseUrl !== undefined && config.apiBaseUrl !== '') {
    script.setAttribute('data-api-base-url', config.apiBaseUrl);
    script.setAttribute('data-api-origins', new URL(config.apiBaseUrl).origin);
  }
  script.setAttribute('data-syncanix-embed', 'true');

  doc.head.appendChild(script);
  return script;
}
